import { InfoOutlined } from '@mui/icons-material';
import { Box, Card, Chip, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CircularProgressLabel from '@/components/shared/circular-progress-label';
import TimeDifference from '@/components/shared/time-difference';
import TooltipModal from '@/components/shared/tooltip-modal';
import type { FlashcardDecksAndStats } from '@/types';

const sxRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  mb: 1,
};

const sxValue = {
  display: 'flex',
  fontWeight: 700,
  textAlign: 'right',
};

export default function FlashcardStats({
  decksAndStats,
  plannedToFinishAt,
  testAt,
}: {
  decksAndStats: FlashcardDecksAndStats;
  plannedToFinishAt: string;
  testAt?: string;
}) {
  const { t } = useTranslation();
  const { newPerDay, newRemaining, newRemainingToday, progress, total } = decksAndStats.stats;
  const review = decksAndStats.decks.reduce((acc, item) => acc + item.deck.stats.review, 0);
  const cardsPerDayWord = t('flashcard.common.card', { count: newPerDay });
  console.log(newPerDay, newRemainingToday, newRemaining);
  return (
    <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
        {t('flashcard.stats.overviewTitle')}
      </Typography>
      <Box sx={sxRow}>
        <strong>{t('flashcard.stats.planTitle')}</strong>
      </Box>
      {testAt && (
        <Box sx={sxRow}>
          {t('flashcard.stats.toTest')}
          <Typography sx={sxValue}>
            <TimeDifference to={testAt} /> {t('flashcard.stats.days')}
          </Typography>
        </Box>
      )}
      {plannedToFinishAt && (
        <Box sx={sxRow}>
          {t('flashcard.stats.toPlannedFinish')}
          <Typography sx={sxValue}>
            <TimeDifference to={plannedToFinishAt} /> {t('flashcard.stats.days')}
          </Typography>
        </Box>
      )}
      <Box sx={sxRow}>
        <Box
          sx={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.5,
            lineHeight: 1.5,
          }}>
          <TooltipModal title={t('flashcard.stats.tooltipPlan')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {t('flashcard.stats.planLabel')} <InfoOutlined />:
            </Box>
          </TooltipModal>
        </Box>
        <Box component="span" sx={sxValue}>
          {t('flashcard.stats.perDay', {
            count: newPerDay,
            cards: cardsPerDayWord,
          })}
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={sxRow}>
        <strong>{t('flashcard.stats.totalProgress')}</strong>
        <CircularProgressLabel value={progress} />
      </Box>
      <Box sx={sxRow}>
        {t('flashcard.stats.allCards')}
        <Box component="span" sx={sxValue}>
          {total} {t('flashcard.common.card', { count: total })}
        </Box>
      </Box>
      <Box sx={sxRow}>
        {t('flashcard.stats.learned')}
        <Box component="span" sx={sxValue}>
          {total - newRemaining} {t('flashcard.common.card', { count: total - newRemaining })}
        </Box>
      </Box>
      <Box sx={sxRow}>
        {t('flashcard.stats.notLearned')}
        <Box component="span" sx={sxValue}>
          {newRemaining} {t('flashcard.common.card', { count: newRemaining })}
        </Box>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={sxRow}>
        <Box component="span" sx={sxValue}>
          {t('flashcard.stats.todayProgress')}
        </Box>
      </Box>
      <Box sx={sxRow}>
        {t('flashcard.stats.newLearnedToday')}
        {newRemainingToday === 0 ? (
          <Chip
            label={t('flashcard.stats.newLearnedTodayDone', {
              done: newPerDay,
              total: newPerDay,
              cards: t('flashcard.common.card', { count: newPerDay }),
            })}
            size="small"
            variant="outlined"
            color="success"
          />
        ) : (
          <Box component="span" sx={sxValue}>
            {t('flashcard.stats.newLearnedTodayOpen', {
              done: newPerDay - newRemainingToday,
              total: newPerDay,
              cards: t('flashcard.common.card', { count: newPerDay }),
            })}
          </Box>
        )}
      </Box>
      <Box sx={sxRow}>
        {t('flashcard.stats.toReview')}
        {review === 0 ? (
          <Chip
            label={t('flashcard.stats.noneOpen')}
            size="small"
            variant="outlined"
            color="success"
          />
        ) : (
          <Box component="span" sx={sxValue}>
            {review} {t('flashcard.common.card', { count: review })}
          </Box>
        )}
      </Box>
    </Card>
  );
}
